import { useRef, useCallback, type SyntheticEvent } from 'react';

type FieldElement = HTMLInputElement | HTMLTextAreaElement;

interface CursorField<T extends FieldElement> {
  ref: React.RefObject<T | null>;
  /** Attach to the field's onSelect to track cursor position */
  trackCursor: (e?: SyntheticEvent<T>) => void;
  /** Insert text at the last-known cursor position */
  insert: (text: string, getValue: () => string, setValue: (v: string) => void) => void;
}

/**
 * Hook to track cursor position and insert text at that position.
 *
 * The key insight: when a merge tag button is clicked, the field loses focus
 * and selectionStart/selectionEnd reset to 0. We save the cursor position
 * on every selection change BEFORE that happens.
 *
 * Uses event.currentTarget to identify the actual element (not just the ref),
 * which handles cases where multiple elements share the same hook instance
 * (e.g., Mantine Tabs with keepMounted panels).
 */
export function useInsertAtCursor<T extends FieldElement>(): CursorField<T> {
  const ref = useRef<T | null>(null);
  const savedPosition = useRef<number>(0);
  const activeElement = useRef<T | null>(null);

  const trackCursor = useCallback((e?: SyntheticEvent<T>) => {
    const el = e?.currentTarget ?? ref.current;
    if (el) {
      activeElement.current = el;
      savedPosition.current = el.selectionStart ?? 0;
    }
  }, []);

  const insert = useCallback(
    (text: string, getValue: () => string, setValue: (v: string) => void) => {
      const pos = savedPosition.current;
      const current = getValue();
      const clamped = Math.min(pos, current.length);
      const newValue = current.substring(0, clamped) + text + current.substring(clamped);
      setValue(newValue);

      // Restore focus and place cursor after the inserted text
      const newPos = clamped + text.length;
      requestAnimationFrame(() => {
        const el = activeElement.current ?? ref.current;
        if (el) {
          el.focus();
          el.selectionStart = newPos;
          el.selectionEnd = newPos;
          savedPosition.current = newPos;
        }
      });
    },
    [],
  );

  return { ref, trackCursor, insert };
}
